import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useMutation, useQuery } from "convex/react";
import { useLanguage } from "../Context/LanguageContext";
import { FiClock, FiTag, FiArrowRight, FiEdit2, FiPlus, FiShield, FiLogOut } from "react-icons/fi";
import { api } from "../convexApi";
import BlogUnavailable from "../Components/BlogUnavailable";
import {
  isBlogBackendConfigured,
  hasStoredAdminKey,
  setStoredAdminKey,
  clearStoredAdminKey,
} from "../services/blogService";
import "./blog.css";

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

const BlogConnected = () => {
  const { t, language } = useLanguage();
  const posts = useQuery(api.blog.list);
  const seedStarterPosts = useMutation(api.blog.seedStarterPosts);
  const [adminEnabled, setAdminEnabled] = useState(hasStoredAdminKey());

  const enableAdminMode = async () => {
    const typedKey = window.prompt(t("EnterAdminKey"));
    if (!typedKey) return;

    const trimmedKey = typedKey.trim();
    setStoredAdminKey(trimmedKey);
    setAdminEnabled(process.env.REACT_APP_BLOG_ADMIN_KEY === trimmedKey);

    // Optionally seed starter posts the first time admin mode is enabled.
    if (posts && posts.length === 0) {
      try {
        await seedStarterPosts({ adminKey: trimmedKey });
      } catch {
        // Ignore seed failures in UI and allow normal reading.
      }
    }
  };

  const disableAdminMode = () => {
    clearStoredAdminKey();
    setAdminEnabled(false);
  };

  if (posts === undefined) {
    return (
      <motion.div
        className="blog-page"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        key={language}
      >
        <div className="blog-page__inner">
          <div className="blog-empty mono">
            <p>{t("LoadingPosts")}</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="blog-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      key={language}
    >
      <div className="blog-page__inner">
        {/* Header */}
        <motion.div
          className="blog-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="blog-header__eyebrow mono">
            <span style={{ color: "var(--text-muted)" }}>{"// "}</span>
            <span style={{ color: "var(--accent-cyan)" }}>{t("BlogPosts")}</span>
          </div>
          <h1 className="blog-header__title">
            {t("BlogTitle")} <span className="blog-header__accent">{t("BlogTitleAccent")}</span>
          </h1>
          <p className="blog-header__subtitle">{t("BlogSub")}</p>

          <div className="blog-admin-row">
            {adminEnabled ? (
              <>
                <Link to="/blog/create" className="blog-primary-btn mono">
                  <FiPlus size={14} /> {t("CreateBlogPost")}
                </Link>
                <button
                  type="button"
                  className="blog-secondary-btn mono"
                  onClick={disableAdminMode}
                  title={adminEnabled ? t("AdminConnected") : t("AdminDisconnected")}
                >
                  <FiLogOut size={14} /> {t("ExitAdmin")}
                </button>
              </>
            ) : (
              <button type="button" className="blog-secondary-btn mono" onClick={enableAdminMode}>
                <FiShield size={14} /> {t("AdminAccess")}
              </button>
            )}
          </div>
        </motion.div>

        {/* Post cards */}
        <div className="blog-grid">
          {posts.map((post, i) => (
            <motion.div
              key={post.id}
              className="blog-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.07 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
            >
              {post.image ? (
                <img src={post.image} alt={post.title} className="blog-card__image" />
              ) : null}
              <div className="blog-card__tags">
                {post.tags.map((tag) => (
                  <span key={`${post.id}-${tag}`} className="blog-card__tag mono">
                    <FiTag size={10} /> {tag}
                  </span>
                ))}
              </div>

              <h3 className="blog-card__title">{post.title}</h3>
              <p className="blog-card__excerpt">{post.excerpt}</p>

              <div className="blog-card__meta">
                <span className="blog-card__meta-item mono">
                  <FiClock size={12} /> {post.readTime}
                </span>
                <span className="blog-card__status mono">
                  {new Date(post.createdAt).toLocaleDateString()}
                </span>

                <div className="blog-card__actions mono">
                  <Link to={`/blog/${post.id}`} className="blog-card__link">
                    {t("Read")} <FiArrowRight size={12} />
                  </Link>
                  {adminEnabled ? (
                    <Link
                      to={`/blog/edit/${post.id}`}
                      className="blog-card__link blog-card__link--edit"
                    >
                      <FiEdit2 size={12} /> {t("Edit")}
                    </Link>
                  ) : null}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const Blog = () => {
  if (!isBlogBackendConfigured) {
    return <BlogUnavailable />;
  }

  return <BlogConnected />;
};

export default Blog;
